// Records a driver-entered odometer reading against a vehicle's "last KM"
// (vehicles.manual_km/manual_km_date) -- the same field FleetManagement.tsx's
// RUC/service/tyre-rotation remaining-km math already reads from. Only
// updates when the new reading is HIGHER than whatever's currently on file
// (from either a completed shift's end_odometer or a prior manual entry),
// so a mis-typed lower reading can never quietly roll back RUC tracking.
import { supabase } from "@/lib/supabase";

export interface LatestOdometer {
  km: number;
  date: string;
  source: "shift" | "manual";
}

// Mirrors FleetManagement.tsx's latestOdoByVehicle resolution rule
// (src/components/FleetManagement.tsx) for a single vehicle: whichever of
// the vehicle's own manual_km/manual_km_date or its most recent completed
// shift's end_odometer has the later date wins. Kept as a separate
// single-vehicle lookup here rather than reusing that bulk-computed map,
// since this needs to run for one vehicle at inspection-submit time.
export async function getLatestOdometerForVehicle(vehicleNumber: string): Promise<LatestOdometer | null> {
  const [{ data: vehicleRow }, { data: shiftRow }] = await Promise.all([
    supabase.from("vehicles").select("manual_km, manual_km_date").eq("vehicle_number", vehicleNumber).maybeSingle(),
    supabase
      .from("driver_shifts")
      .select("end_odometer, shift_date")
      .eq("vehicle_number", vehicleNumber)
      .not("end_odometer", "is", null)
      .order("shift_date", { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  const shiftResult: LatestOdometer | null =
    shiftRow?.end_odometer != null ? { km: shiftRow.end_odometer, date: shiftRow.shift_date, source: "shift" } : null;

  const manualResult: LatestOdometer | null =
    vehicleRow?.manual_km != null && vehicleRow?.manual_km_date != null
      ? { km: vehicleRow.manual_km, date: vehicleRow.manual_km_date, source: "manual" }
      : null;

  if (!shiftResult) return manualResult;
  if (!manualResult) return shiftResult;
  return new Date(manualResult.date) > new Date(shiftResult.date) ? manualResult : shiftResult;
}

/** Returns whether the vehicle's last KM was actually updated. */
export async function recordInspectionOdometer(
  vehicleNumber: string,
  kmReading: number,
  driverName?: string | null,
): Promise<boolean> {
  const current = await getLatestOdometerForVehicle(vehicleNumber);
  if (current && kmReading <= current.km) return false;

  const today = new Date().toISOString().slice(0, 10);
  const { error } = await supabase
    .from("vehicles")
    .update({ manual_km: kmReading, manual_km_date: today, manual_km_driver: driverName || null })
    .eq("vehicle_number", vehicleNumber);

  if (error) {
    console.error("Error recording inspection odometer:", error);
    return false;
  }
  return true;
}
